import type { ChangeEvent } from 'react'
import type { CatalogDocumentDTO } from '@/shared/api'
import { Icon } from '@/shared/ui/Icon'
import { DocumentCard } from './DocumentCard'
import { isDocumentRetrievable, scopeEquals, scopeFromDocument } from '@/features/scope'
import { useScope } from '@/features/scope'
import type { DocumentStatusFilter } from './documentUtils'

const STATUS_FILTER_OPTIONS: { value: DocumentStatusFilter; label: string }[] = [
  { value: 'all', label: 'Todos os status' },
  { value: 'indexed', label: 'Indexados' },
  { value: 'processing', label: 'Processando' },
  { value: 'failed', label: 'Falhou' },
  { value: 'unknown', label: 'Desconhecido' },
]

interface Props {
  documents: CatalogDocumentDTO[]
  totalCount: number
  status: 'idle' | 'loading' | 'ready' | 'error'
  error: string | null
  query: string
  statusFilter: DocumentStatusFilter
  onQueryChange: (value: string) => void
  onStatusFilterChange: (value: DocumentStatusFilter) => void
  onRetry: () => void
  onUseAsScope: (doc: CatalogDocumentDTO) => void
  /** Step 7.9C — caller opens the confirm modal for this document. */
  onRequestDelete: (doc: CatalogDocumentDTO) => void
  /** ``document_id`` currently in-flight for delete, if any. */
  deletingDocumentId: string | null
}

/**
 * Renders the documents grid plus its loading / error / empty states. The
 * grid is plain CSS — no virtualization yet — because the catalog returns a
 * single page per tenant and counts are small.
 *
 * The toolbar (search + status filter) lives here so the parent stays a
 * thin orchestrator; the filtering itself happens before this component is
 * called so the count badge in the section header stays authoritative.
 */
export function DocumentList({
  documents,
  totalCount,
  status,
  error,
  query,
  statusFilter,
  onQueryChange,
  onStatusFilterChange,
  onRetry,
  onUseAsScope,
  onRequestDelete,
  deletingDocumentId,
}: Props) {
  const { selected: activeScope } = useScope()

  const isFiltering =
    query.trim().length > 0 || statusFilter !== 'all'

  const toolbar = (
    <div className="doc-toolbar" role="search">
      <label className="doc-search">
        <Icon name="search" />
        <input
          type="search"
          value={query}
          placeholder="Buscar por nome, document_id, entidade ou grupo…"
          onChange={(e: ChangeEvent<HTMLInputElement>) => onQueryChange(e.target.value)}
          onKeyDown={(e) => {
            // The library search and the upload <form> share the same workspace
            // but are sibling subtrees. Enter inside this search input should
            // never bubble out and accidentally trigger an upload submit, even
            // if a future refactor wraps the page in a form.
            if (e.key === 'Enter') e.preventDefault()
          }}
          spellCheck={false}
          aria-label="Buscar documentos"
        />
      </label>
      <select
        className="doc-status-filter"
        value={statusFilter}
        onChange={(e) => onStatusFilterChange(e.target.value as DocumentStatusFilter)}
        aria-label="Filtrar por status"
      >
        {STATUS_FILTER_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  )

  if (status === 'loading' || status === 'idle') {
    return (
      <>
        {toolbar}
        <div className="doc-grid doc-grid-skeleton" aria-busy="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="doc-card doc-card-skeleton">
              <div className="skeleton-line long" />
              <div className="skeleton-line mid" />
              <div className="skeleton-line short" />
            </div>
          ))}
        </div>
      </>
    )
  }

  if (status === 'error') {
    return (
      <div className="doc-state doc-state-error" role="alert">
        <div className="doc-state-icon"><Icon name="close" /></div>
        <h3>Não foi possível carregar a biblioteca</h3>
        <p>{error ?? 'Erro inesperado ao acessar o catálogo.'}</p>
        <button type="button" className="doc-state-cta" onClick={onRetry}>
          <Icon name="rotate" /> Tentar novamente
        </button>
      </div>
    )
  }

  if (totalCount === 0) {
    return (
      <div className="doc-state doc-state-empty">
        <div className="doc-state-icon"><Icon name="doc" /></div>
        <h3>Nenhum documento indexado ainda</h3>
        <p>Faça upload para começar — use o painel ao lado.</p>
      </div>
    )
  }

  if (documents.length === 0) {
    // Catalog has documents, but the current filters hide all of them.
    return (
      <>
        {toolbar}
        <div className="doc-state doc-state-empty">
          <div className="doc-state-icon"><Icon name="search" /></div>
          <h3>Nenhum documento corresponde à busca</h3>
          <p>Ajuste o texto ou o filtro de status para tentar novamente.</p>
          {isFiltering ? (
            <button
              type="button"
              className="doc-state-cta"
              onClick={() => {
                onQueryChange('')
                onStatusFilterChange('all')
              }}
            >
              <Icon name="close" /> Limpar filtros
            </button>
          ) : null}
        </div>
      </>
    )
  }

  return (
    <>
      {toolbar}
      <div className="doc-grid">
        {documents.map((doc) => {
          const scopeForDoc = scopeFromDocument(doc)
          const isActive = scopeEquals(activeScope, scopeForDoc)
          return (
            <DocumentCard
              key={`${doc.tenant_id}::${doc.document_id}`}
              doc={doc}
              isActiveScope={isActive}
              canUseAsScope={isDocumentRetrievable(doc)}
              onUseAsScope={() => onUseAsScope(doc)}
              onRequestDelete={() => onRequestDelete(doc)}
              isDeleting={deletingDocumentId === doc.document_id}
            />
          )
        })}
      </div>
    </>
  )
}
